// RITA-1498 (Nutzungserfassung Phase 1b) — Versand eines Bündels an POST /usage/events.
//
// fetch mit keepalive (überlebt das Verlassen der Seite) und dem normalen JWT im Header.
// sendBeacon scheidet aus: es kann keinen Authorization-Header setzen, und der Token liegt
// nicht im Cookie (Konzept 4.2). Die Statusklassen folgen dem Serververtrag in
// apps/backend/src/middleware/usageEvents.ts.
import type { IUsageBatch, IUsageEvent } from 'global-intern-types';
import type { FlushReason, SendOutcome } from './batchQueue';

export const USAGE_EVENTS_ENDPOINT = '/usage/events';

export type FetchLike = (input: string, init: RequestInit) => Promise<{ status: number; ok: boolean }>;

export interface SenderDeps {
  fetchImpl: FetchLike;
  getToken: () => string | null;
  buildBatch: (events: IUsageEvent[]) => IUsageBatch;
  endpoint?: string;
}

export interface Sender {
  send(events: IUsageEvent[], reason: FlushReason): Promise<SendOutcome>;
}

export const classifyStatus = (status: number): SendOutcome => {
  if (status === 204) return 'stop';
  if (status >= 200 && status < 300) return 'ok';
  if (status === 401) return 'stop';
  if (status === 409) return 'session_conflict';
  if (status >= 500) return 'retry';
  return 'drop';
};

export const createSender = ({
  fetchImpl,
  getToken,
  buildBatch,
  endpoint = USAGE_EVENTS_ENDPOINT,
}: SenderDeps): Sender => ({
  async send(events: IUsageEvent[]): Promise<SendOutcome> {
    const token = getToken();
    if (!token) return 'stop';
    try {
      const response = await fetchImpl(endpoint, {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify(buildBatch(events)),
        keepalive: true,
      });
      return classifyStatus(response.status);
    } catch {
      return 'retry';
    }
  },
});
